/**
 * Focus management for a non-native dialog: trap Tab inside it while open, and
 * hand focus back to the control that opened it on close.
 *
 * Without this a keyboard user Tabs straight out of the popover into the page
 * behind it (which is still fully interactive), and closing strands them at the
 * top of the document instead of on the trigger they just used.
 *
 * Deliberately does NOT steal focus on open: the composer carries autoFocus, so
 * moving focus here would fight it. Focus is only placed if it is not already
 * inside the dialog.
 *
 * Mirrored in the extension as content/help/useDialogFocus.ts; keep behavior in
 * sync (the two suites mirror each other).
 */
import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

export function useDialogFocus(
  isOpen: boolean,
  dialogRef: RefObject<HTMLElement | null>,
  returnFocusRef: RefObject<HTMLElement | null>,
): void {
  // Place focus inside the dialog if nothing there has it yet.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (dialog.contains(document.activeElement)) return;
    focusableWithin(dialog)[0]?.focus();
  }, [isOpen, dialogRef]);

  // Trap Tab / Shift+Tab.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = focusableWithin(dialog);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // Focus outside the dialog (or on its edges) wraps back inside.
      if (!active || !dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, dialogRef]);

  // Restore focus to the trigger on the open -> closed transition (the cleanup
  // of the open state), so a keyboard user lands back on the control they used
  // rather than at the top of the document.
  useEffect(() => {
    if (!isOpen) return;
    return () => {
      returnFocusRef.current?.focus();
    };
  }, [isOpen, returnFocusRef]);
}
